import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const CookiePolicy = () => {
    return (
        <div className="bg-white text-gray-800 overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('img/Privacy/Cookie.png')",
                    }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
                </div>

                <div className="relative z-10 container mx-auto px-4 py-20 text-left">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 leading-tight">
                            Cookie Policy
                        </h1>

                        {/* Breadcrumb */}
                        <div className="flex items-center space-x-2 mb-6">
                            <Link to="/" className="text-white/80 hover:text-white transition-colors text-sm font-medium">
                                Home
                            </Link>
                            <ChevronRightIcon className="h-4 w-4 text-white/60" />
                            <span className="text-yellow-300 text-sm font-semibold">Cookie Policy</span>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Content Section */}
            <section className="py-20 px-6 md:px-12 lg:px-20">
                <div className="max-w-4xl mx-auto space-y-12">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-primary-900 mb-6 border-b pb-4">Introduction</h2>
                        <div className="prose prose-blue max-w-none text-gray-700 space-y-6">
                            <p>
                                At AHAM Housing Finance Limited, we believe in being clear and open about how we collect and use data related to you. In the spirit of transparency, this policy provides detailed information about how and when we use cookies on our website.
                            </p>
                            <p>
                                When you visit our website, we may collect information from you automatically through cookies or similar technology. This helps us provide you with a good experience when you browse our website and also allows us to improve our site.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-primary-900 mb-6 border-b pb-4">What are Cookies?</h2>
                        <div className="prose prose-blue max-w-none text-gray-700 space-y-6">
                            <p>
                                Cookies are small text files that are placed on your computer or mobile device by websites that you visit. They are widely used to make websites work, or work more efficiently, as well as to provide information to the owners of the site.
                            </p>
                            <p>
                                Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device when you go offline, while session cookies are deleted as soon as you close your web browser.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-primary-900 mb-6 border-b pb-4">How AHAM Uses Cookies</h2>
                        <div className="prose prose-blue max-w-none text-gray-700 space-y-6">
                            <p>We use cookies for several reasons, including:</p>
                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-xl font-semibold mb-3 text-primary-800">1. Essential Cookies</h3>
                                    <p>These cookies are strictly necessary to provide you with services available through our website and to use some of its features, such as access to secure areas. Because these cookies are strictly necessary to deliver the website, you cannot refuse them without impacting how our website functions.</p>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold mb-3 text-primary-800">2. Performance and Analytics Cookies</h3>
                                    <p>These cookies collect information that is used in aggregate form to help us understand how our website is being used, how effective our marketing campaigns are, or to help us customize our website for you. We use these to track visits to our loan product pages and eligibility tools to ensure they work correctly for our users.</p>
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold mb-3 text-primary-800">3. Functional Cookies</h3>
                                    <p>These are used to recognize you when you return to our website. This enables us to personalize our content for you and remember your preferences (for example, your choice of language or region).</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-primary-900 mb-6 border-b pb-4">Controlling Cookies</h2>
                        <div className="prose prose-blue max-w-none text-gray-700 space-y-6">
                            <p>
                                You have the right to decide whether to accept or reject cookies. You can set or amend your web browser controls to accept or refuse cookies. If you choose to reject cookies, you may still use our website though your access to some functionality and areas of our website may be restricted.
                            </p>
                            <p>
                                Most web browsers allow some control of most cookies through the browser settings. To find out more about cookies, including how to see what cookies have been set, visit <a href="https://allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">allaboutcookies.org</a>.
                            </p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl font-bold text-primary-900 mb-6 border-b pb-4">Contact Us</h2>
                        <p className="text-gray-700 mb-6">If you have any questions about our use of cookies or other technologies, please email us at:</p>
                        <ul className="space-y-4">
                            <li className="flex items-center space-x-3">
                                <span className="font-semibold text-primary-800">Email:</span>
                                <a href="mailto:info@ahamhfc.com" className="text-primary-600 hover:underline">info@ahamhfc.com</a>
                            </li>
                            <li className="flex items-center space-x-3">
                                <span className="font-semibold text-primary-800">Phone:</span>
                                <a href="tel:07823911555" className="text-primary-600 hover:underline">07823 911555</a>
                            </li>
                        </ul>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default CookiePolicy;
